import { useEffect, useState } from 'react'

// Strict YYYY-MM-DD wrapper around <input type="date">. Native date inputs
// already constrain typing to digits, but browsers display the segmented
// input in the OS locale (DD/MM/YYYY in en-ZA, MM/DD/YYYY in en-US, etc.) —
// operators on mixed devices were typing the right digits in the wrong
// segments and ending up with flipped dates. This wrapper:
//
// - shows an inline "YYYY-MM-DD" hint so the canonical format is visible
// - commits a value to the parent on every `change` event that parses
//   cleanly + falls within [min, max], so a Save click immediately after
//   picking a date doesn't drop the change (operators were back-dating
//   pallets and then losing the backdate because the parent's state was
//   still on the old value when Save fired)
// - on blur with an invalid value, snaps back to the last good value (or
//   empty) and surfaces a one-line error
//
// Returns empty string for "no date picked", same shape as a bare input
// type="date".
interface Props {
  value:       string                 // YYYY-MM-DD or ''
  /** What this date IS — "Packed on", "Tipped on". Required on purpose: every
   *  caller already prints a caption ABOVE the field without associating it,
   *  so the control itself announced only "date". */
  label:       string
  onChange:    (next: string) => void
  min?:        string
  max?:        string
  className?:  string
  required?:   boolean
  /** If true, hide the inline format hint (parent already has its own). */
  hideHint?:   boolean
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

function isValidIsoDate(s: string): boolean {
  if (!ISO_DATE.test(s)) return false
  const [y, m, d] = s.split('-').map(Number)
  if (m < 1 || m > 12 || d < 1 || d > 31) return false
  // Round-trip check for things like Feb 30 → JS would normalize.
  const dt = new Date(Date.UTC(y, m - 1, d))
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d
}

export default function DateInput({
  value, label, onChange, min, max, className, required, hideHint = false,
}: Props) {
  const [draft, setDraft] = useState(value)
  const [error, setError] = useState<string | null>(null)

  // Re-sync if the parent updates the value externally (e.g. reset).
  useEffect(() => {
    setDraft(value)
    setError(null)
  }, [value])

  // Pure validator — returns 'ok' for committable values, an error
  // message for out-of-range / malformed values, and 'partial' for
  // mid-edit input we shouldn't yelp about (e.g. only YY entered so far).
  function validate(next: string): 'ok' | 'partial' | string {
    if (next === '') return 'ok'
    if (!ISO_DATE.test(next)) return 'partial'
    if (!isValidIsoDate(next)) return 'Date must be YYYY-MM-DD (e.g. 2026-05-07).'
    if (min && next < min) return `Date can't be before ${min}.`
    if (max && next > max) return `Date can't be after ${max}.`
    return 'ok'
  }

  // Commit on change so a Save click immediately after picking a date
  // doesn't drop the change. Partial / out-of-range stays local and surfaces
  // an error only on blur (avoids yelping mid-edit).
  function handleChange(next: string) {
    setDraft(next)
    const result = validate(next)
    if (result === 'ok') {
      setError(null)
      onChange(next)
    } else if (result === 'partial') {
      setError(null)
    } else {
      setError(result)
    }
  }

  // On blur, snap back to last good value when the input is left in an
  // invalid state. Re-commit good values too so the parent ends up
  // synced regardless of whether onChange already did it.
  function handleBlur(next: string) {
    const result = validate(next)
    if (result === 'ok') {
      setError(null)
      onChange(next)
    } else if (result === 'partial') {
      setDraft(value)
      setError(null)
    } else {
      setDraft(value)
      setError(result)
    }
  }

  return (
    <div>
      <input
        type="date"
        aria-label={label}
        value={draft}
        min={min}
        max={max}
        required={required}
        className={className}
        onChange={e => handleChange(e.target.value)}
        onBlur={e => handleBlur(e.target.value)}
      />
      {!hideHint && !error && (
        <p className="text-[10px] max-sm:text-xs text-fg-muted mt-1">Format: YYYY-MM-DD</p>
      )}
      {error && (
        <p className="text-[10px] max-sm:text-xs text-amber-300 mt-1">{error}</p>
      )}
    </div>
  )
}
